import * as React from "react";
import { useCallback, useMemo } from "react";
import { RouteComponentProps, withRouter } from "react-router";
import { Image, Loader, Pagination, Table } from "semantic-ui-react";

import * as DTO from "../../../interfaces";
import { PricePlanTier, WebSettings } from "@/interfaces";
import { SocialNetworksSection } from "@/components";
import {
  PlayerPlanStatus,
  PlayerSearchDir,
  PlayerSearchResult,
  PlayerSearchSort,
  PlayerSegmentationType,
  PlayersServices,
} from "@/services/bedrock";
import {
  URLS,
  useAccountSettings,
  printBasicDate,
  getLargeSubscriptionType,
  useSocialNetworks,
  useSegmentation,
  exportPlayerList,
  usePlayerSearch,
  useDebounced,
  SegmentationOption,
} from "@/utils";

import { Svg } from "@/assets";
import { capitalize } from "lodash";
import { Combobox, Button, InputGroup, Input, Badge, PlayerCell } from "@/components/ui";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

type Props = RouteComponentProps;

const PlayersFC: React.FC<Props> = ({ history }) => {
  const { hasAccountSetting, hasWritePermission } = useAccountSettings();

  const hasSCPlusTier = hasAccountSetting(WebSettings.SC_PLUS_TIER);
  const withWritePermission = hasWritePermission(DTO.WebComponents.PLAYERS);

  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const debouncedSearchTerm = useDebounced(searchTerm, 500);
  const [page, setPage] = React.useState<number>(1);
  const { socialNetworks: coachSns } = useSocialNetworks(false);

  type ComboboxItem = { key: string; label: string; value: string };

  const { segmentationOptions } = useSegmentation(["REGION", "BRANCH", "COST_CENTER", "STATE"]);
  const [selectedSegments, setSelectedSegments] = React.useState<SegmentationOption[]>([]);
  const [selectedStatus, setSelectedStatus] = React.useState<ComboboxItem>();
  const statusOptions: ComboboxItem[] = [
    { key: "ACTIVE", label: "Active", value: "ACTIVE" },
    { key: "CANCELED", label: "Canceled", value: "CANCELED" },
  ];
  const effectiveStatuses: PlayerPlanStatus[] = useMemo(() => {
    if (selectedStatus?.key == "CANCELED") return ["CANCELED", "INACTIVE"];
    else if (selectedStatus?.key == "ACTIVE") return ["ACTIVE", "FREE_TRIAL"];
    else return [];
  }, [selectedStatus]);
  const [selectedTier, setSelectedTier] = React.useState<ComboboxItem>();
  const tierOptions: ComboboxItem[] = [
    { key: "PLAYER_BASIC", label: "Basic", value: "PLAYER_BASIC" },
    { key: "PLAYER_PLUS", label: "SocialCoach+", value: "PLAYER_PLUS" },
  ];
  const [sortColumn, setSortColumn] = React.useState<PlayerSearchSort>("created_date");
  const [sortDirection, setSortDirection] = React.useState<PlayerSearchDir>("desc");

  const { searchPlayers } = usePlayerSearch();
  const { players, isFetching } = searchPlayers({
    page,
    perPage: 40,
    searchTerm: debouncedSearchTerm,
    planTiers: selectedTier ? [selectedTier.value as PricePlanTier] : undefined,
    segmentationIds: selectedSegments.map(s => s.value as string),
    planStatuses: effectiveStatuses,
    sort: sortColumn,
    sortDirection: sortDirection,
  });

  const totalPages = useMemo(() => players?.pagination?.totalPages || 1, [players?.pagination?.totalPages]);

  const exportPlayers = useCallback(async () => {
    let percentComplete = 0;
    const toastId = toast.loading(`Exporting... (${percentComplete}%)`);
    let exportPage = 1;
    let hasMorePages = true;
    let allResults: PlayerSearchResult[] = [];
    while (hasMorePages) {
      const nextResults = await PlayersServices.searchPlayers({
        perPage: 100,
        segmentationIds: selectedSegments.map(s => s.value as string),
        planStatuses: effectiveStatuses,
        page: exportPage,
        searchTerm,
      });
      allResults.push(...nextResults.content);
      hasMorePages = nextResults.pagination.totalPages > exportPage;
      percentComplete = (exportPage / nextResults.pagination.totalPages) * 100;
      toast.loading(`Exporting... (${percentComplete.toFixed(0)}%)`, { id: toastId });
      exportPage++;
    }
    toast.success("Export complete.", { id: toastId });
    exportPlayerList(allResults);
  }, [effectiveStatuses, selectedSegments, searchTerm]);

  const getSegmentNames = (player: PlayerSearchResult, types: PlayerSegmentationType[]) => {
    const segments = player.segmentationDetails
      ?.filter(s => types.includes(s.type))
      ?.sort((a, b) => b.type.localeCompare(a.type) || a.name.localeCompare(b.name));

    if (segments?.length > 0)
      return (
        <div className="flex flex-row flex-wrap gap-1">
          {segments?.map(s => (
            <Badge
              key={`segmentBadge-${s.type}-${s.name}`}
              size={"small"}
              variant={"outline"}
              className={s.type == "REGION" ? "bg-pink-100" : "bg-blue-100"}
            >
              <div className={`font-medium`}>{s.name}</div>
            </Badge>
          ))}
        </div>
      );
    else return "";
  };

  const playerSortFields = ["first_name", "last_name", "email"];

  const handlePlayerSort = () => {
    setPage(1);

    if (!playerSortFields.includes(sortColumn)) {
      setSortColumn("first_name");
      setSortDirection("asc");
      return;
    }

    if (sortDirection === "asc") {
      setSortDirection("desc");
    } else {
      setSortDirection("asc");
      if (sortColumn === "first_name") setSortColumn("last_name");
      else if (sortColumn === "last_name") setSortColumn("email");
      else setSortColumn("first_name"); // Loop back to start
    }
  };

  const getPlayerSortLabel = () => {
    if (!playerSortFields.includes(sortColumn)) return null;

    let label = "";
    switch (sortColumn) {
      case "first_name":
        label = "First Name";
        break;
      case "last_name":
        label = "Last Name";
        break;
      case "email":
        label = "Email";
        break;
    }
    return <span className="font-normal text-xs ml-1 text-muted-foreground">(Sorted by {label})</span>;
  };

  const handleSort = (clickedColumn: PlayerSearchSort) => {
    if (sortColumn !== clickedColumn) {
      setSortColumn(clickedColumn);
      setSortDirection("asc");
      setPage(1);
      return;
    }

    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
  };

  const updateSearch = (s: string) => {
    setSearchTerm(s);
    setPage(1);
  };

  const updateSegmentation = (v: SegmentationOption[]) => {
    setSelectedSegments(v);
    setPage(1);
  };

  const updateStatus = (v: ComboboxItem | null) => {
    setSelectedStatus(v ?? undefined);
    setPage(1);
  };

  const updateTier = (v: ComboboxItem | null) => {
    setSelectedTier(v ?? undefined);
    setPage(1);
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4 flex-wrap">
          <InputGroup
            className="w-50"
            control={controlProps => (
              <Input {...controlProps} placeholder="Search Player..." onChange={e => updateSearch(e.target.value)} />
            )}
            append={<Search />}
          />

          <Combobox
            className="min-w-50 max-w-80"
            searchable
            placeholder="Filter by Segmentation"
            items={segmentationOptions}
            value={selectedSegments}
            itemToStringLabel={item => item.text || item.value}
            itemToBadge={item => item.label}
            onValueChange={value => updateSegmentation(value)}
            multiple
            maxChips="auto"
          />

          <Combobox
            className="min-w-50 max-w-80"
            placeholder="Filter by Status"
            items={statusOptions}
            value={selectedStatus}
            showClear
            onValueChange={value => updateStatus(value)}
          />

          {hasSCPlusTier && (
            <Combobox
              className="min-w-50 max-w-80"
              placeholder="Filter by Plan"
              items={tierOptions}
              value={selectedTier}
              showClear
              onValueChange={value => updateTier(value)}
            />
          )}
          {isFetching && <Loader active inline size="tiny" />}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" children="Export" onClick={exportPlayers} />
          {withWritePermission && <Button children="Add Player" onClick={() => history.push(URLS.coach.newPlayer)} />}
        </div>
      </div>

      <Table striped sortable compact="very">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell
              sorted={
                playerSortFields.includes(sortColumn)
                  ? sortDirection === "asc"
                    ? "ascending"
                    : "descending"
                  : undefined
              }
              onClick={handlePlayerSort}
              colSpan={2}
            >
              Player {getPlayerSortLabel()}
            </Table.HeaderCell>
            <Table.HeaderCell className="cursor-default pointer-events-none">Status</Table.HeaderCell>
            <Table.HeaderCell className="cursor-default pointer-events-none">Socials</Table.HeaderCell>
            <Table.HeaderCell className="cursor-default pointer-events-none">
              <div className={"flex flex-row gap-1"}>
                <Badge size={"small"} variant={"outline"} className={"bg-pink-100"}>
                  Regions
                </Badge>
                <div>/</div>
                <Badge size={"small"} variant={"outline"} className={"bg-blue-100"}>
                  Branches
                </Badge>
              </div>
            </Table.HeaderCell>
            <Table.HeaderCell
              sorted={
                sortColumn === "created_date" ? (sortDirection === "asc" ? "ascending" : "descending") : undefined
              }
              onClick={() => handleSort("created_date")}
            >
              Added
            </Table.HeaderCell>
            <Table.HeaderCell
              sorted={
                sortColumn === "last_login_date" ? (sortDirection === "asc" ? "ascending" : "descending") : undefined
              }
              onClick={() => handleSort("last_login_date")}
            >
              Last Login
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {players?.content?.map(player => {
            const isCancelled = ["CANCELED", "INACTIVE"].includes(player.planStatus);
            const playerLink = URLS.coach.editPlayer.replace(":playerId", `${player.id}`);
            return (
              <Table.Row key={`playerRow-${player.id}`}>
                <Table.Cell>
                  <Link to={playerLink}>
                    <Image circular size="mini" src={player.photoUrl ? player.photoUrl : Svg.noImageuser} />
                  </Link>
                </Table.Cell>
                <Table.Cell>
                  <PlayerCell player={player} />
                </Table.Cell>

                <Table.Cell>
                  <Link to={playerLink}>
                    <div>
                      {capitalize(player.planStatus)}
                      {isCancelled && ` ${printBasicDate(player.canceledAt)}`}
                    </div>

                    {hasSCPlusTier && getLargeSubscriptionType(player.tier)}
                  </Link>
                </Table.Cell>

                <Table.Cell>
                  <SocialNetworksSection
                    actives={player.activeSocialNetworkProfiles.map(s => s.socialNetworkId)}
                    allSocialNetworks={coachSns}
                  />
                </Table.Cell>

                <Table.Cell>{getSegmentNames(player, ["REGION", "BRANCH"])}</Table.Cell>

                <Table.Cell>
                  <div>{printBasicDate(player.createdDate!)}</div>
                </Table.Cell>
                <Table.Cell>
                  <div>{printBasicDate(player.lastLoginDate!)}</div>
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
      <div className="flex justify-center">
        <Pagination
          activePage={page}
          onPageChange={(_, { activePage }) => setPage(activePage as number)}
          totalPages={totalPages}
          disabled={isFetching}
          pointing
          secondary
        />
      </div>
    </div>
  );
};

export const Players = withRouter(PlayersFC);
